// Order-tracking data layer.
//
// TODAY: order state comes from our own `shipments` rows (updated by admin /
// fulfillment webhooks). WHEN BASK HEADLESS API ACCESS LANDS: implement
// fetchBaskOrderTracking() against their orders endpoint and merge the live
// carrier events into OrderTracking — the UI (OrderTracker component) only
// consumes this shape, so nothing downstream changes.

import type { ShipmentRow, ShipmentStatus } from "@/lib/database.types";

export type OrderStageKey = "ordered" | "shipped" | "in_transit" | "delivered";

export interface OrderStage {
  key: OrderStageKey;
  label: string;
  at: string | null;   // ISO timestamp when the stage was reached
  reached: boolean;
}

export interface OrderTracking {
  shipmentId: string;
  stages: OrderStage[];
  /** Index into stages of the furthest reached stage. */
  currentIndex: number;
  exception: "returned" | "lost" | null;
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
}

const STATUS_INDEX: Record<ShipmentStatus, number> = {
  pending: 0,
  shipped: 1,
  in_transit: 2,
  delivered: 3,
  returned: 2, // exception badges override display
  lost: 2,
};

export function carrierTrackingUrl(
  carrier: string | null,
  trackingNumber: string | null,
): string | null {
  if (!trackingNumber) return null;
  const c = (carrier ?? "").toLowerCase();
  if (c.includes("ups")) return `https://www.ups.com/track?tracknum=${trackingNumber}`;
  if (c.includes("usps")) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
  if (c.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
  return `https://www.google.com/search?q=${encodeURIComponent(`${carrier ?? ""} tracking ${trackingNumber}`.trim())}`;
}

export function trackingFromShipment(shipment: ShipmentRow): OrderTracking {
  const idx = STATUS_INDEX[shipment.status];
  const stages: OrderStage[] = [
    { key: "ordered",    label: "Ordered",    at: shipment.created_at,   reached: idx >= 0 },
    { key: "shipped",    label: "Shipped",    at: shipment.shipped_at,   reached: idx >= 1 },
    { key: "in_transit", label: "In transit", at: null,                  reached: idx >= 2 },
    { key: "delivered",  label: "Delivered",  at: shipment.delivered_at, reached: idx >= 3 },
  ];
  return {
    shipmentId: shipment.id,
    stages,
    currentIndex: idx,
    exception:
      shipment.status === "returned" ? "returned"
      : shipment.status === "lost" ? "lost"
      : null,
    carrier: shipment.carrier,
    trackingNumber: shipment.tracking_number,
    trackingUrl: carrierTrackingUrl(shipment.carrier, shipment.tracking_number),
  };
}
